import { Calendar } from '@/components/ui/calendar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { normalizeSearch } from '@/lib/search';
import { todayIsoDate } from '@/lib/travel-esim';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon, Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';

const toIsoDate = (date: Date) => format(date, 'yyyy-MM-dd');
const shortDate = (iso: string) => format(parseISO(iso), 'd MMM', { locale: fr });
const longDate = (iso: string) =>
  format(parseISO(iso), 'd MMM yyyy', { locale: fr });

const fieldButtonClass =
  'flex h-11 w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-left text-sm transition-colors hover:border-emerald-200 cursor-pointer';

/** cmdk's default match is accent-sensitive; « bresil » must find « Brésil ». */
function matchDestination(value: string, search: string): number {
  return normalizeSearch(value).includes(normalizeSearch(search)) ? 1 : 0;
}

export function DestinationPicker({
  destinations,
  isLoading,
  value,
  onChange,
}: {
  destinations: string[];
  isLoading: boolean;
  value: string | undefined;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={fieldButtonClass} disabled={isLoading}>
          <span className={cn('flex-1 truncate', !value && 'text-slate-400')}>
            {value ?? (isLoading ? 'Chargement...' : 'Choisir un pays')}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command filter={matchDestination}>
          <CommandInput placeholder="Rechercher un pays..." />
          <CommandList>
            <CommandEmpty>Aucune destination</CommandEmpty>
            <CommandGroup>
              {destinations.map((country) => (
                <CommandItem
                  key={country}
                  value={country}
                  onSelect={() => {
                    onChange(country);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      country === value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {country}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function StayPicker({
  departureDate,
  returnDate,
  onChange,
}: {
  departureDate: string | undefined;
  returnDate: string | undefined;
  onChange: (range: { from?: string; to?: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  // A past departure cannot be ordered: those days are greyed out.
  const today = parseISO(todayIsoDate());
  const selected: DateRange | undefined = departureDate
    ? {
        from: parseISO(departureDate),
        to: returnDate ? parseISO(returnDate) : undefined,
      }
    : undefined;
  const label =
    departureDate && returnDate
      ? `du ${shortDate(departureDate)} au ${longDate(returnDate)}`
      : departureDate
        ? `du ${longDate(departureDate)} au …`
        : 'Départ et retour';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={fieldButtonClass}>
          <CalendarIcon className="h-4 w-4 shrink-0 text-slate-400" />
          <span className={cn('flex-1 truncate', !departureDate && 'text-slate-400')}>
            {label}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          locale={fr}
          numberOfMonths={2}
          selected={selected}
          disabled={{ before: today }}
          fromMonth={today}
          defaultMonth={selected?.from ?? today}
          onSelect={(range) => {
            onChange({
              from: range?.from ? toIsoDate(range.from) : undefined,
              to: range?.to ? toIsoDate(range.to) : undefined,
            });
            if (range?.from && range?.to) setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
